import { pick } from 'lodash';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import {
  BULK_JOB_POST,
  CAMPAIGN,
  IMAGE_FIELDS,
  IMAGE_VARIANTS,
  PERKS,
  REWARD,
  SINGLE_JOB_POST,
} from '../../util/constants';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { ListingState } from '../../util/enums';
import { storableError } from '../../util/errors';
import { entityRefs, getImageVariantConfigs } from '../../util/genericHelpers';
import { getBrandListingsQueryParams, getListingCategory } from '../../util/listingPageHelpers';
import { parse } from '../../util/urlHelpers';
import { getAssociatedListingId } from '../../util/userDataExtraction';

export const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === ListingState.PUBLISHED)
    .map(l => l.id);
};
const RESULT_PAGE_SIZE = 60;
// ================ Action types ================ //
export const SET_INITIAL_VALUES = 'app/BrandOfferingPage/SET_INITIAL_VALUES';

export const SEARCH_LISTINGS_REQUEST = 'app/BrandOfferingPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/BrandOfferingPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/BrandOfferingPage/SEARCH_LISTINGS_ERROR';

export const FETCH_PERKS_LISTINGS_REQUEST = 'app/BrandOfferingPage/FETCH_PERKS_LISTINGS_REQUEST';
export const FETCH_PERKS_LISTINGS_SUCCESS = 'app/BrandOfferingPage/FETCH_PERKS_LISTINGS_SUCCESS';
export const FETCH_PERKS_LISTINGS_ERROR = 'app/BrandOfferingPage/FETCH_PERKS_LISTINGS_ERROR';

export const FETCH_CAMPAIGN_LISTINGS_REQUEST =
  'app/BrandOfferingPage/FETCH_CAMPAIGN_LISTINGS_REQUEST';
export const FETCH_CAMPAIGN_LISTINGS_SUCCESS =
  'app/BrandOfferingPage/FETCH_CAMPAIGN_LISTINGS_SUCCESS';
export const FETCH_CAMPAIGN_LISTINGS_ERROR = 'app/BrandOfferingPage/FETCH_CAMPAIGN_LISTINGS_ERROR';

export const FETCH_REWARD_LISTINGS_REQUEST = 'app/BrandOfferingPage/FETCH_REWARD_LISTINGS_REQUEST';
export const FETCH_REWARD_LISTINGS_SUCCESS = 'app/BrandOfferingPage/FETCH_REWARD_LISTINGS_SUCCESS';
export const FETCH_REWARD_LISTINGS_ERROR = 'app/BrandOfferingPage/FETCH_REWARD_LISTINGS_ERROR';

export const FETCH_SINGLE_JOBS_LISTINGS_REQUEST =
  'app/BrandOfferingPage/FETCH_SINGLE_JOBS_LISTINGS_REQUEST';
export const FETCH_SINGLE_JOBS_LISTINGS_SUCCESS =
  'app/BrandOfferingPage/FETCH_SINGLE_JOBS_LISTINGS_SUCCESS';
export const FETCH_SINGLE_JOBS_LISTINGS_ERROR =
  'app/BrandOfferingPage/FETCH_SINGLE_JOBS_LISTINGS_ERROR';

export const FETCH_BULK_JOBS_LISTINGS_REQUEST =
  'app/BrandOfferingPage/FETCH_BULK_JOBS_LISTINGS_REQUEST';
export const FETCH_BULK_JOBS_LISTINGS_SUCCESS =
  'app/BrandOfferingPage/FETCH_BULK_JOBS_LISTINGS_SUCCESS';
export const FETCH_BULK_JOBS_LISTINGS_ERROR =
  'app/BrandOfferingPage/FETCH_BULK_JOBS_LISTINGS_ERROR';

export const FETCH_LISTINGS_DATA = {
  REQUEST: 'app/BrandOfferingPage/FETCH_LISTINGS_DATA_REQUEST',
  SUCCESS: 'app/BrandOfferingPage/FETCH_LISTINGS_DATA_SUCCESS',
  ERROR: 'app/BrandOfferingPage/FETCH_LISTINGS_DATA_ERROR',
};

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  fetchPerksListingsInProgress: false,
  fetchPerksListingsError: null,
  perksListings: [],
  fetchJobsListingsInProgress: false,
  fetchJobsListingsError: null,
  singleJobListings: [],
  bulkJobListings: [],
  fetchCampaignListingsInProgress: false,
  fetchCampaignListingsError: null,
  campaignListings: [],
  fetchRewardListingsInProgress: false,
  fetchRewardListingsError: null,
  rewardListings: [],
  fetchListingDataInProgress: false,
  fetchListingDataError: null,
  listingData: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };
    case FETCH_PERKS_LISTINGS_REQUEST:
      return { ...state, fetchPerksListingsInProgress: true, fetchPerksListingsError: null };
    case FETCH_PERKS_LISTINGS_SUCCESS:
      return { ...state, fetchPerksListingsInProgress: false, perksListings: payload };
    case FETCH_PERKS_LISTINGS_ERROR:
      return { ...state, fetchPerksListingsInProgress: false, fetchPerksListingsError: payload };

    case FETCH_SINGLE_JOBS_LISTINGS_REQUEST:
      return { ...state, fetchJobsListingsInProgress: true, fetchJobsListingsError: null };
    case FETCH_SINGLE_JOBS_LISTINGS_SUCCESS:
      return { ...state, fetchJobsListingsInProgress: false, singleJobListings: payload };
    case FETCH_SINGLE_JOBS_LISTINGS_ERROR:
      return { ...state, fetchJobsListingsInProgress: false, fetchJobsListingsError: payload };

    case FETCH_BULK_JOBS_LISTINGS_REQUEST:
      return { ...state, fetchJobsListingsInProgress: true, fetchJobsListingsError: null };
    case FETCH_BULK_JOBS_LISTINGS_SUCCESS:
      return { ...state, fetchJobsListingsInProgress: false, bulkJobListings: payload };
    case FETCH_BULK_JOBS_LISTINGS_ERROR:
      return { ...state, fetchJobsListingsInProgress: false, fetchJobsListingsError: payload };
    case FETCH_CAMPAIGN_LISTINGS_REQUEST:
      return { ...state, fetchCampaignListingsInProgress: true, fetchCampaignListingsError: null };
    case FETCH_CAMPAIGN_LISTINGS_SUCCESS:
      return { ...state, fetchCampaignListingsInProgress: false, campaignListings: payload };
    case FETCH_CAMPAIGN_LISTINGS_ERROR:
      return {
        ...state,
        fetchCampaignListingsInProgress: false,
        fetchCampaignListingsError: payload,
      };
    case FETCH_REWARD_LISTINGS_REQUEST:
      return { ...state, fetchRewardListingsInProgress: true, fetchRewardListingsError: null };
    case FETCH_REWARD_LISTINGS_SUCCESS:
      return { ...state, fetchRewardListingsInProgress: false, rewardListings: payload };
    case FETCH_REWARD_LISTINGS_ERROR:
      return {
        ...state,
        fetchRewardListingsInProgress: false,
        fetchRewardListingsError: payload,
      };
    case FETCH_LISTINGS_DATA.REQUEST:
      return {
        ...state,
        fetchListingDataInProgress: true,
        fetchListingDataError: null,
        listingData: null,
      };
    case FETCH_LISTINGS_DATA.SUCCESS:
      return {
        ...state,
        fetchListingDataInProgress: false,
        fetchListingDataError: null,
        listingData: payload,
      };
    case FETCH_LISTINGS_DATA.ERROR:
      return {
        ...state,
        fetchListingDataInProgress: false,
        fetchListingDataError: payload,
        listingData: null,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const currentPageResultIdsSelector = state => state.BrandOfferingPage.currentPageResultIds;
export const paginationSelector = state => state.BrandOfferingPage.pagination;

export const perksListingsSelector = state => state.BrandOfferingPage.perksListings;
export const singleJobsListingsSelector = state => state.BrandOfferingPage.singleJobListings;
export const bulkJobsListingsSelector = state => state.BrandOfferingPage.bulkJobListings;
export const campaignListingsSelector = state => state.BrandOfferingPage.campaignListings;
export const rewardListingsSelector = state => state.BrandOfferingPage.rewardListings;

export const listingDataSelector = state => state.BrandOfferingPage.listingData;

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});
export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchPerksListingsRequest = () => ({ type: FETCH_PERKS_LISTINGS_REQUEST });
export const fetchPerksListingsSuccess = data => ({
  type: FETCH_PERKS_LISTINGS_SUCCESS,
  payload: data,
});
export const fetchPerksListingsError = e => ({
  type: FETCH_PERKS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchSingleJobsListingsRequest = () => ({ type: FETCH_SINGLE_JOBS_LISTINGS_REQUEST });
export const fetchSingleJobsListingsSuccess = data => ({
  type: FETCH_SINGLE_JOBS_LISTINGS_SUCCESS,
  payload: data,
});
export const fetchSingleJobsListingsError = e => ({
  type: FETCH_SINGLE_JOBS_LISTINGS_REQUEST,
  error: true,
  payload: e,
});

export const fetchBulkJobsListingsRequest = () => ({ type: FETCH_BULK_JOBS_LISTINGS_REQUEST });
export const fetchBulkJobsListingsSuccess = data => ({
  type: FETCH_BULK_JOBS_LISTINGS_SUCCESS,
  payload: data,
});
export const fetchBulkJobsListingsError = e => ({
  type: FETCH_BULK_JOBS_LISTINGS_ERROR,
  error: true,
  payload: e,
});
export const fetchCampaignListingsRequest = () => ({ type: FETCH_CAMPAIGN_LISTINGS_REQUEST });
export const fetchCampaignListingsSuccess = data => ({
  type: FETCH_CAMPAIGN_LISTINGS_SUCCESS,
  payload: data,
});
export const fetchCampaignListingsError = e => ({
  type: FETCH_CAMPAIGN_LISTINGS_ERROR,
  error: true,
  payload: e,
});
export const fetchRewardListingsRequest = () => ({ type: FETCH_REWARD_LISTINGS_REQUEST });
export const fetchRewardListingsSuccess = data => ({
  type: FETCH_REWARD_LISTINGS_SUCCESS,
  payload: data,
});
export const fetchRewardListingsError = e => ({
  type: FETCH_REWARD_LISTINGS_ERROR,
  error: true,
  payload: e,
});
export const fetchListingsDataRequest = () => ({
  type: FETCH_LISTINGS_DATA.REQUEST,
});

export const fetchListingsDataSuccess = data => ({
  type: FETCH_LISTINGS_DATA.SUCCESS,
  payload: data,
});

export const fetchListingsDataError = e => ({
  type: FETCH_LISTINGS_DATA.ERROR,
  error: true,
  payload: e,
});

export const fetchListingsData = catergory => async (dispatch, getState, sdk) => {
  dispatch(fetchListingsDataRequest());

  try {
    const listings = [];
    const batchSize = 10;
    const delay = 500;

    const queryParams = {
      page: 1,
      perPage: batchSize,
      pub_listingCategory: catergory,
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData',
      ],
    };

    const firstResponse = await sdk.listings.query(queryParams);
    const { data, meta } = firstResponse.data || {};
    const totalPages = meta?.totalPages || 0;
    if (!data?.length) {
      dispatch(fetchListingsDataSuccess());
      return;
    }

    let entities = updatedEntities({}, firstResponse.data);
    listings.push(...denormalisedEntities(entities, entityRefs(data), false));

    for (let i = 2; i <= totalPages; i++) {
      const res = await sdk.listings.query({ ...queryParams, page: i });
      entities = updatedEntities(entities, res.data);
      listings.push(...denormalisedEntities(entities, entityRefs(res.data?.data || []), false));
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    // 🔧 Flatten all nested criteria arrays
    const criteria = listings
      .flatMap(l => l.attributes?.publicData?.criteria || [])
      .filter(Boolean);
    // ✅ Deduplicate
    const uniqueCriteria = Array.from(new Set(criteria));

    const listingData = {
      criteria: uniqueCriteria,
    };
    dispatch(fetchListingsDataSuccess(listingData));
    return { criteria: uniqueCriteria };
  } catch (err) {
    dispatch(fetchListingsDataError(storableError(err)));
    throw err;
  }
};
// ================ Thunks ================ //

export const searchListings = (searchParams, config) => async (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));
  try {
    // Extract image configuration
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = IMAGE_VARIANTS.PREFIX,
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    // Build API parameters
    const params = {
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images', 'currentStock'],
      'fields.image': [
        ...IMAGE_FIELDS,
        `variants.${variantPrefix}`, // Cropped variants
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
      ],
      'fields.listing': [
        'title',
        'description',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
      ],
      ...getImageVariantConfigs(variantPrefix, aspectRatio),
    };
    const updatedParams = { ...params, ...searchParams };
    // Make API call
    const response = await sdk.listings.query(updatedParams);

    // Process response
    const listingFields = config?.listing?.listingFields || [];
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(searchListingsSuccess(response));

    return response;
  } catch (error) {
    const errorToStore = storableError(error);
    dispatch(searchListingsError(errorToStore));
    throw errorToStore;
  }
};

export const fetchListingsGeneric = (
  categorySuffix,
  requestAction,
  successAction,
  errorAction,
  params = {}
) => (listingType, associatedListingId) => async (dispatch, getState, sdk) => {
  dispatch(requestAction());
  const queryParams = getBrandListingsQueryParams();
  try {
    const response = await sdk.listings.query({
      pub_listingCategory: getListingCategory(categorySuffix, listingType),
      ...queryParams,
      ...params,
      // ...(associatedListingId ? { pub_parentListingId: associatedListingId } : {}),
    });
    const entities = updatedEntities({}, response.data);
    const listingRefs = entityRefs(response.data.data);
    const denormalisedListings = denormalisedEntities(entities, listingRefs, false);
    dispatch(successAction(denormalisedListings));
  } catch (error) {
    dispatch(errorAction(storableError(error)));
  }
};

// Using the generic function to create specific thunks
export const fetchPerksListings = fetchListingsGeneric(
  PERKS,
  fetchPerksListingsRequest,
  fetchPerksListingsSuccess,
  fetchPerksListingsError
);

export const fetchSingleJobsListings = fetchListingsGeneric(
  SINGLE_JOB_POST,
  fetchSingleJobsListingsRequest,
  fetchSingleJobsListingsSuccess,
  fetchSingleJobsListingsError
);
export const fetchBulkJobsListings = fetchListingsGeneric(
  BULK_JOB_POST,
  fetchBulkJobsListingsRequest,
  fetchBulkJobsListingsSuccess,
  fetchBulkJobsListingsError
);
export const fetchCampaignListings = fetchListingsGeneric(
  CAMPAIGN,
  fetchCampaignListingsRequest,
  fetchCampaignListingsSuccess,
  fetchCampaignListingsError
);
export const fetchRewardListings = fetchListingsGeneric(
  REWARD,
  fetchRewardListingsRequest,
  fetchRewardListingsSuccess,
  fetchRewardListingsError
);
export const loadData = (params, search, config) => async (dispatch, getState) => {
  dispatch(
    setInitialValues({
      perksListings: [],
      singleJobListings: [],
      bulkJobListings: [],
      campaignListings: [],
      rewardListings: [],
    })
  );

  const queryParams = parse(search);
  const { page = 1, jobCategoryFilter, ...rest } = queryParams;
  const searchParams = {
    page,
    ...rest,
  };
  const currentUser = await dispatch(fetchCurrentUser());
  const associatedListingId = getAssociatedListingId(currentUser);
  [
    fetchPerksListings,
    fetchSingleJobsListings,
    fetchBulkJobsListings,
    fetchCampaignListings,
    fetchRewardListings,
  ].forEach(fetchAction => {
    dispatch(fetchAction(queryParams.pub_listingCategory, associatedListingId));
  });
  return dispatch(searchListings(searchParams, config));
};
